import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { toast, Toaster } from 'sonner';
import {
  X, ArrowLeft, Send, Loader2, CheckCircle2, Frame, Mail, ExternalLink,
  ChevronLeft, ChevronRight, ZoomIn, Plus, Minus, Globe, ShieldCheck, Lock, Headphones,
} from 'lucide-react';
import CustomCursor from '@/components/CustomCursor';
import Header from '@/components/Header';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CURRENCY_SYMBOL = { EUR: '€', USD: '$', GBP: '£', PLN: 'zł' };

function formatPrice(amount, currency) {
  if (amount == null || amount === '') return 'Price on request';
  const sym = CURRENCY_SYMBOL[currency] || '';
  const n = Number(amount);
  if (Number.isNaN(n)) return 'Price on request';
  if (currency === 'PLN') return `${n.toLocaleString('pl-PL', { minimumFractionDigits: 0 })} ${sym}`;
  return `${sym}${n.toLocaleString('en-US', { minimumFractionDigits: 0 })}`;
}

function editionText(a) {
  if (a.edition_label) return a.edition_label;
  if (a.edition_total && a.edition_available != null) return `Edition of ${a.edition_total} · ${a.edition_available} available`;
  if (a.edition_total) return `Edition of ${a.edition_total}`;
  return null;
}

const pad = (n) => String(n).padStart(2, '0');

function dimsText(a) {
  if (a.width_cm && a.height_cm) {
    let s = `${Number(a.width_cm)} × ${Number(a.height_cm)}`;
    if (a.depth_cm) s += ` × ${Number(a.depth_cm)}`;
    return `${s} cm`;
  }
  return a.dimensions || null;
}

const MATERIAL_DESC = {
  paper: 'Archival, museum-grade paper selected for exceptional depth, surface quality and longevity.',
  print: 'Pigment giclée printing for a precise tonal range and archival permanence.',
  frame: 'Minimal construction designed to complement the work without visually competing with it.',
  glazing: 'Protective glazing that reduces reflection and shields the print over time.',
};

function materialRows(a) {
  const rows = [];
  if (a.paper) rows.push({ key: 'paper', label: 'Paper', value: a.paper, desc: MATERIAL_DESC.paper });
  if (a.medium) rows.push({ key: 'print', label: 'Print', value: a.medium, desc: MATERIAL_DESC.print });
  if (a.frame_material) rows.push({ key: 'frame', label: 'Frame', value: [a.frame_material, a.frame_colour].filter(Boolean).join(' · '), desc: MATERIAL_DESC.frame });
  if (a.glazing) rows.push({ key: 'glazing', label: 'Glazing', value: a.glazing, desc: MATERIAL_DESC.glazing });
  return rows;
}

// ─── Motion helpers ───────────────────────────────────────────

function Reveal({ children, className = '', delay = 0 }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduce ? false : { opacity: 0, y: 16 }}
      whileInView={reduce ? {} : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

function SectionHead({ index, label, heading, sub }) {
  return (
    <Reveal>
      <div className="flex items-baseline gap-4 mb-5">
        <span className="text-[10px] font-mono text-[#90E0EF] tracking-[0.3em]">{pad(index)}</span>
        <span className="h-px flex-1 max-w-[40px] bg-[#222]" />
        <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#777]">{label}</span>
      </div>
      {heading && (
        <h2 className="text-2xl md:text-4xl font-outfit font-semibold text-white tracking-tight leading-[1.08] max-w-3xl">
          {heading}
        </h2>
      )}
      {sub && (
        <p className="mt-5 text-[14px] md:text-[15px] leading-relaxed text-[#b4b4b4] max-w-2xl whitespace-pre-line">
          {sub}
        </p>
      )}
    </Reveal>
  );
}

function Section({ children, className = '', testid }) {
  return (
    <section data-testid={testid} className={`border-t border-[#111] px-6 md:px-10 lg:px-16 py-20 md:py-28 ${className}`}>
      <div className="max-w-6xl mx-auto">{children}</div>
    </section>
  );
}

// ─── Shared 16:9 media carousel (Look Closer / In Context) ────

function MediaCarousel({ items, testid }) {
  const [i, setI] = useState(0);
  const touch = useRef(null);
  if (!items || !items.length) return null;
  const many = items.length > 1;
  const prev = () => setI((x) => (x - 1 + items.length) % items.length);
  const next = () => setI((x) => (x + 1) % items.length);
  const it = items[i];
  return (
    <div data-testid={testid}>
      <div
        className="relative aspect-video w-full overflow-hidden border border-[#141414] bg-[#0a0a0a] group"
        onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (touch.current == null) return;
          const dx = e.changedTouches[0].clientX - touch.current;
          if (dx > 40) prev(); else if (dx < -40) next();
          touch.current = null;
        }}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.img
            key={it.image_url}
            src={it.image_url}
            alt={it.alt_text || it.caption || ''}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="w-full h-full object-cover"
          />
        </AnimatePresence>
        {many && (
          <>
            <button onClick={prev} aria-label="Previous" className="absolute left-3 top-1/2 -translate-y-1/2 p-2 bg-[#050505]/70 border border-[#1f1f1f] text-white opacity-0 group-hover:opacity-100 transition-opacity"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={next} aria-label="Next" className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-[#050505]/70 border border-[#1f1f1f] text-white opacity-0 group-hover:opacity-100 transition-opacity"><ChevronRight className="w-4 h-4" /></button>
          </>
        )}
      </div>
      {(it.caption || many) && (
        <div className="mt-3 flex items-center justify-between gap-4">
          <p className="text-[11px] font-mono text-[#888] leading-relaxed">{it.caption || ''}</p>
          {many && (
            <div className="flex gap-1.5 shrink-0">
              {items.map((_, x) => (
                <button key={x} onClick={() => setI(x)} aria-label={`View ${x + 1}`} className={`h-px transition-all ${x === i ? 'w-8 bg-[#90E0EF]' : 'w-4 bg-[#333] hover:bg-[#666]'}`} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── 02 Film ──────────────────────────────────────────────────

function FilmSection({ num, heading, body, url, poster }) {
  return (
    <Section testid="section-film">
      <SectionHead index={num} label="Film" heading={heading || 'Film'} sub={body} />
      <Reveal className="mt-10">
        <div className="relative aspect-video w-full overflow-hidden border border-[#141414] bg-black" data-testid="artwork-film">
          <video src={url} poster={poster || undefined} controls preload="metadata" playsInline className="w-full h-full object-contain bg-black" />
        </div>
      </Reveal>
    </Section>
  );
}

// ─── 03 Look Closer ───────────────────────────────────────────

function LookCloserSection({ num, heading, body, items }) {
  return (
    <Section testid="section-look-closer">
      <SectionHead index={num} label="Look Closer" heading={heading || 'Look closer'} sub={body} />
      <Reveal className="mt-10"><MediaCarousel items={items} testid="artwork-look-closer" /></Reveal>
    </Section>
  );
}

// ─── 04 The Object ────────────────────────────────────────────

function ObjectAccordion({ items }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="border-t border-[#1a1a1a]">
      {items.map((it, idx) => {
        const isOpen = open === idx;
        return (
          <div key={it.key} className="border-b border-[#1a1a1a]">
            <button
              onClick={() => setOpen(isOpen ? -1 : idx)}
              data-testid={`object-row-${it.key}`}
              className="w-full flex items-center justify-between gap-4 py-5 text-left"
            >
              <span className="flex items-baseline gap-3 sm:gap-5 min-w-0">
                <span className="text-[9px] font-mono uppercase tracking-[0.24em] text-[#666] w-14 sm:w-16 shrink-0">{it.label}</span>
                <span className="text-[14px] md:text-[15px] text-white">{it.value}</span>
              </span>
              <span className="text-[#90E0EF] shrink-0">{isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}</span>
            </button>
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden"
                >
                  <p className="pb-5 pl-[68px] sm:pl-[84px] text-[13px] leading-relaxed text-[#aaaaaa] max-w-2xl">{it.desc}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}

function ObjectSection({ num, heading, body, rows, image }) {
  return (
    <Section testid="section-object">
      <SectionHead index={num} label="The Object" heading={heading || 'Crafted for a longer tomorrow'} sub={body} />
      {image ? (
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <Reveal>
            <div className="aspect-video overflow-hidden border border-[#141414] bg-[#0a0a0a]">
              <img src={image.image_url} alt={image.caption || 'Frame detail'} className="w-full h-full object-cover" />
            </div>
            {image.caption && <p className="mt-3 text-[11px] font-mono text-[#888]">{image.caption}</p>}
          </Reveal>
          <Reveal delay={0.08}><ObjectAccordion items={rows} /></Reveal>
        </div>
      ) : (
        <Reveal className="mt-10 max-w-3xl"><ObjectAccordion items={rows} /></Reveal>
      )}
    </Section>
  );
}

// ─── 05 Edition & Certification ───────────────────────────────

function EditionNumbers({ total, current }) {
  if (!total || total > 30) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-8" data-testid="edition-numbers">
      {Array.from({ length: total }, (_, k) => k + 1).map((n) => {
        const active = current && n === current;
        return (
          <span key={n} className={`text-[11px] font-mono px-2 py-1 border transition-colors ${active ? 'border-[#90E0EF] text-[#90E0EF] bg-[rgba(144,224,239,0.08)]' : 'border-[#222] text-[#555]'}`}>
            {pad(n)}
          </span>
        );
      })}
    </div>
  );
}

function EditionSection({ num, a, heading, body }) {
  const rows = [
    a.edition_total && { label: 'Edition', value: `Edition of ${a.edition_total}` },
    a.year && { label: 'Year', value: a.year },
    dimsText(a) && { label: 'Size', value: dimsText(a) },
    (a.medium || a.paper) && { label: 'Medium', value: [a.medium, a.paper].filter(Boolean).join(' on ') },
    a.signed && { label: 'Signed', value: `Hand-signed by ${a.artist?.name || 'the artist'}` },
    (a.numbered && a.edition_number) && { label: 'Numbered', value: `Edition ${pad(a.edition_number)}${a.edition_total ? ` of ${a.edition_total}` : ''}` },
    a.certificate_included && { label: 'Certificate', value: 'Certificate of authenticity included' },
  ].filter(Boolean);

  return (
    <Section testid="section-edition">
      <SectionHead index={num} label="Edition & Certification" heading={heading || 'A limited edition with a lasting story'} sub={body || a.certification_text} />
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
        <Reveal>
          <dl className="border-t border-[#1a1a1a]">
            {rows.map((r) => (
              <div key={r.label} className="grid grid-cols-[96px_1fr] sm:grid-cols-[120px_1fr] items-baseline gap-4 py-3.5 border-b border-[#1a1a1a]">
                <dt className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#666]">{r.label}</dt>
                <dd className="text-[13px] md:text-[14px] text-[#e8e8e8]">{r.value}</dd>
              </div>
            ))}
          </dl>
          <EditionNumbers total={a.edition_total} current={a.edition_number} />
        </Reveal>
        {a.certificate_image_url && (
          <Reveal delay={0.08}>
            <div className="aspect-[4/3] overflow-hidden border border-[#141414] bg-[#0a0a0a]">
              <img src={a.certificate_image_url} alt="Certificate of authenticity" className="w-full h-full object-contain" />
            </div>
            <p className="mt-3 text-[11px] font-mono text-[#888]">Certificate of authenticity</p>
          </Reveal>
        )}
      </div>
    </Section>
  );
}

// ─── 06 In Context ────────────────────────────────────────────

function InContextSection({ num, heading, body, items }) {
  return (
    <Section testid="section-in-context">
      <SectionHead index={num} label="In Context" heading={heading || 'In a space of its own'} sub={body || 'View the work at scale.'} />
      <Reveal className="mt-10"><MediaCarousel items={items} testid="artwork-in-context" /></Reveal>
    </Section>
  );
}

// ─── 07 Exhibited / Provenance ────────────────────────────────

function ProvenanceSection({ num, heading, records }) {
  return (
    <Section testid="section-provenance">
      <SectionHead index={num} label="Exhibited" heading={heading || 'Provenance'} />
      <div className="mt-8 border-t border-[#1a1a1a] max-w-3xl">
        {records.map((p, i) => (
          <Reveal key={i}>
            <div className="py-5 border-b border-[#1a1a1a]" data-testid={`provenance-row-${i}`}>
              <div className="flex items-baseline justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-[15px] text-white">{p.event_name || p.institution}</p>
                  <p className="text-[12px] font-mono text-[#888] mt-1">
                    {[p.event_name && p.institution ? p.institution : null, [p.city, p.country].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
                  </p>
                </div>
                {p.year && <span className="text-[12px] font-mono text-[#90E0EF] shrink-0">{p.year}</span>}
              </div>
              {p.url && (
                <a href={p.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-2 text-[11px] font-mono text-[#666] hover:text-white transition-colors">
                  Visit <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

// ─── 08 Acquisition ───────────────────────────────────────────

const REASSURANCE = [
  { icon: Globe, label: 'Worldwide delivery' },
  { icon: ShieldCheck, label: 'Fully insured shipping' },
  { icon: Headphones, label: 'Collector support' },
  { icon: Lock, label: 'Secure private purchase' },
];

function AcquisitionSection({ num, a, heading, body, soldOut, onInquire }) {
  return (
    <Section testid="section-acquisition" className="pb-28 md:pb-36">
      <SectionHead index={num} label="Acquisition" heading={heading || 'Acquire this edition'} sub={body} />
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-20 items-start">
        <Reveal>
          <dl className="border-t border-[#1a1a1a]">
            <div className="py-4 border-b border-[#1a1a1a]">
              <dt className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#666]">Price</dt>
              <dd className="mt-1.5 text-2xl md:text-3xl font-outfit text-white">{formatPrice(a.price_amount, a.currency)}</dd>
            </div>
            {editionText(a) && (
              <div className="py-4 border-b border-[#1a1a1a]">
                <dt className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#666]">Edition</dt>
                <dd className="mt-1.5 text-[14px] text-[#e8e8e8]">
                  {editionText(a)}
                  {(a.signed || a.certificate_included) && (
                    <span className="block text-[12px] text-[#888] mt-0.5">
                      {[a.signed && 'Signed', a.certificate_included && 'Certified'].filter(Boolean).join(' + ')}
                    </span>
                  )}
                </dd>
              </div>
            )}
            <div className="py-4 border-b border-[#1a1a1a]">
              <dt className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#666]">Delivery</dt>
              <dd className="mt-1.5 text-[14px] text-[#e8e8e8]">Worldwide delivery, fully insured</dd>
            </div>
          </dl>
        </Reveal>

        <Reveal delay={0.08}>
          <button
            data-testid="pw-inquiry-btn"
            disabled={soldOut}
            onClick={onInquire}
            className="group w-full inline-flex items-center justify-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] px-6 py-4 transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-40"
            style={{ color: '#050505', background: soldOut ? '#333' : '#90E0EF' }}
          >
            <Mail className="w-4 h-4" />
            {soldOut ? 'Sold out' : 'Inquire about this work'}
          </button>
          <p className="mt-3 text-[10px] font-mono text-[#555] text-center">
            A private enquiry — our team will be in touch to arrange everything.
          </p>
          <div className="mt-8 grid grid-cols-2 gap-x-6 gap-y-4">
            {REASSURANCE.map((r) => (
              <div key={r.label} className="flex items-center gap-2.5">
                <r.icon className="w-3.5 h-3.5 text-[#90E0EF] shrink-0" />
                <span className="text-[11px] font-mono text-[#999]">{r.label}</span>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </Section>
  );
}

// ─── Page ─────────────────────────────────────────────────────

export default function PublicArtworkPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [artwork, setArtwork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [success, setSuccess] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [heroIndex, setHeroIndex] = useState(0);

  useEffect(() => {
    setLoading(true);
    setHeroIndex(0);
    axios.get(`${API}/public/artworks/${slug}`)
      .then((r) => setArtwork(r.data))
      .catch((e) => setError(e?.response?.status === 404 ? 'Artwork not found' : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] text-[#f6f6f6]">
        <CustomCursor /> <Header />
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-[#90E0EF] animate-spin" />
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] text-[#f6f6f6]">
        <CustomCursor /> <Header />
        <div className="min-h-screen flex items-center justify-center px-6">
          <div className="text-center">
            <Frame className="w-8 h-8 text-[#333] mx-auto" />
            <p className="text-[13px] font-mono text-[#888] mt-4">{error}</p>
            <button onClick={() => navigate('/artworks')} className="mt-6 text-[11px] font-mono uppercase tracking-[0.2em] text-[#90E0EF] hover:text-white transition-colors">
              ← BACK TO ARTWORKS
            </button>
          </div>
        </div>
      </div>
    );
  }

  const a = artwork;
  const sections = a.sections || {};
  const media = a.section_media || {};
  const soldOut = !!a.is_sold_out;
  const artistSlug = a.artist?.slug;
  const galleryImages = (a.images && a.images.length > 0)
    ? a.images
    : (a.media_url ? [{ image_url: a.media_url, alt_text: a.title }] : []);
  const hero = galleryImages[heroIndex]?.image_url || a.media_url;
  const en = (t) => !!sections[t]?.enabled;
  const matRows = materialRows(a);

  const metaParts = [a.year, dimsText(a), editionText(a)].filter(Boolean);

  // Build the ordered, conditional section list (hero is 01, rest follow).
  const blocks = [];
  if (en('film') && a.film_url) {
    blocks.push((n) => <FilmSection key="film" num={n} heading={sections.film?.heading} body={sections.film?.body} url={a.film_url} poster={a.film_poster_url} />);
  }
  if (en('look_closer') && (media.look_closer || []).length) {
    blocks.push((n) => <LookCloserSection key="lc" num={n} heading={sections.look_closer?.heading} body={sections.look_closer?.body} items={media.look_closer} />);
  }
  if (en('object') && (matRows.length || sections.object?.body || (media.object || []).length)) {
    blocks.push((n) => <ObjectSection key="obj" num={n} heading={sections.object?.heading} body={sections.object?.body} rows={matRows} image={(media.object || [])[0]} />);
  }
  if (en('edition') && (a.edition_total || a.signed || a.numbered || a.certificate_included || sections.edition?.body || a.certification_text)) {
    blocks.push((n) => <EditionSection key="ed" num={n} a={a} heading={sections.edition?.heading} body={sections.edition?.body} />);
  }
  if (en('in_context') && (media.in_context || []).length) {
    blocks.push((n) => <InContextSection key="ic" num={n} heading={sections.in_context?.heading} body={sections.in_context?.body} items={media.in_context} />);
  }
  if ((a.provenance || []).length) {
    blocks.push((n) => <ProvenanceSection key="prov" num={n} heading={sections.provenance?.heading} records={a.provenance} />);
  }
  // Acquisition always closes the story.
  blocks.push((n) => <AcquisitionSection key="acq" num={n} a={a} heading={sections.acquisition?.heading} body={sections.acquisition?.body} soldOut={soldOut} onInquire={() => setInquiryOpen(true)} />);

  return (
    <div className="min-h-screen bg-[#050505] text-[#f6f6f6]">
      <CustomCursor />
      <Header />
      <Toaster theme="dark" position="bottom-center" toastOptions={{ style: { background: '#111', border: '1px solid #333', borderRadius: 0, fontFamily: '"JetBrains Mono", monospace', fontSize: '12px' } }} />

      <main className="relative z-10" style={{ paddingTop: 'calc(var(--header-height) + 24px)' }}>
        {/* Back strip */}
        <div className="px-6 md:px-10 lg:px-16 py-4 border-b border-[#111]">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] text-[#666] hover:text-white transition-colors" data-testid="artwork-back">
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
            <a href="/artworks" className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#666] hover:text-white transition-colors">
              All artworks →
            </a>
          </div>
        </div>

        {/* ── 01 THE WORK ── */}
        <section className="px-6 md:px-10 lg:px-16 pt-12 md:pt-16 pb-20 md:pb-28" data-testid="section-the-work">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1.25fr_1fr] gap-12 lg:gap-16 items-center">
            {/* Artwork */}
            <Reveal>
              <div className="relative flex items-center justify-center min-h-[42vh] lg:min-h-[64vh]">
                <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 58% 52% at 50% 44%, rgba(144,224,239,0.08), rgba(144,224,239,0.02) 46%, transparent 72%)' }} />
                <div className="relative w-full flex flex-col items-center">
                  {hero && (
                    <img
                      src={hero}
                      alt={a.title}
                      onClick={() => setLightboxIndex(heroIndex)}
                      data-testid="artwork-hero-image"
                      className="relative max-h-[58vh] w-auto max-w-full object-contain cursor-zoom-in"
                      style={{ filter: 'drop-shadow(0 40px 55px rgba(0,0,0,0.5))' }}
                    />
                  )}
                  {hero && (
                    <div aria-hidden className="relative -mt-1 w-full flex justify-center overflow-hidden h-16 md:h-24">
                      <img
                        src={hero}
                        alt=""
                        className="max-h-[58vh] w-auto max-w-full object-contain -scale-y-100 opacity-[0.13]"
                        style={{ maskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.65), transparent 58%)', WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.65), transparent 58%)' }}
                      />
                    </div>
                  )}
                </div>
                {galleryImages.length > 1 && (
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 flex gap-2">
                    {galleryImages.map((im, i) => (
                      <button
                        key={i}
                        onClick={() => setHeroIndex(i)}
                        aria-label={`View ${i + 1}`}
                        data-testid={`hero-thumb-${i}`}
                        className={`w-12 h-12 border overflow-hidden transition-colors ${i === heroIndex ? 'border-[#90E0EF]' : 'border-[#222] hover:border-[#444]'}`}
                      >
                        <img src={im.image_url} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </Reveal>

            {/* Info */}
            <Reveal delay={0.1}>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#90E0EF]">
                  {artistSlug ? (
                    <a href={`/artists/${artistSlug}`} className="hover:text-white transition-colors inline-flex items-center gap-1.5">
                      {a.artist?.name}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (
                    <span>{a.artist?.name}</span>
                  )}
                </div>

                <h1 className="mt-4 text-4xl md:text-5xl font-outfit font-semibold text-white leading-[1.03] tracking-tight">
                  {a.title}
                </h1>

                {metaParts.length > 0 && (
                  <p className="mt-5 text-[11px] font-mono uppercase tracking-[0.22em] text-[#888]">
                    {metaParts.join('  ·  ')}
                  </p>
                )}

                {soldOut && (
                  <span className="inline-block mt-5 border border-[#c89dff]/40 px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-[#c89dff]">
                    Sold out
                  </span>
                )}

                {a.description && (
                  <p className="mt-7 text-[14px] md:text-[15px] leading-relaxed text-[#c4c4c4] whitespace-pre-line max-w-xl">
                    {a.description}
                  </p>
                )}

                <div className="mt-9">
                  <button
                    data-testid="pw-hero-inquiry-btn"
                    disabled={soldOut}
                    onClick={() => setInquiryOpen(true)}
                    className="group inline-flex items-center gap-2.5 text-[11px] font-mono uppercase tracking-[0.2em] px-7 py-4 border transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-40"
                    style={{ color: soldOut ? '#888' : '#90E0EF', borderColor: soldOut ? '#333' : 'rgba(144,224,239,0.5)' }}
                  >
                    <Mail className="w-4 h-4" />
                    {soldOut ? 'Sold out' : 'Inquire about this work'}
                  </button>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ── Modular story (02 → 08) ── */}
        {blocks.map((render, idx) => render(idx + 2))}
      </main>

      <AnimatePresence>
        {inquiryOpen && (
          <InquiryModal
            artwork={a}
            onClose={() => { setInquiryOpen(false); if (success) setSuccess(false); }}
            success={success}
            setSuccess={setSuccess}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {lightboxIndex !== null && galleryImages.length > 0 && (
          <Lightbox images={galleryImages} startIndex={lightboxIndex} onClose={() => setLightboxIndex(null)} />
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Fullscreen lightbox ──────────────────────────────────────

function Lightbox({ images, startIndex, onClose }) {
  const [index, setIndex] = useState(startIndex || 0);
  const touchStartX = useRef(null);
  const prev = useCallback(() => setIndex((i) => (i - 1 + images.length) % images.length), [images.length]);
  const next = useCallback(() => setIndex((i) => (i + 1) % images.length), [images.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [prev, next, onClose]);

  const hasMany = images.length > 1;
  const image = images[index];

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] bg-[#050505]/95 backdrop-blur-md flex items-center justify-center"
      onClick={onClose}
      onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchStartX.current == null) return;
        const dx = e.changedTouches[0].clientX - touchStartX.current;
        if (dx > 40) prev(); else if (dx < -40) next();
        touchStartX.current = null;
      }}
      data-testid="artwork-lightbox"
    >
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 md:px-8 py-4 pointer-events-none">
        <span className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#888]">
          {hasMany ? `${pad(index + 1)} / ${pad(images.length)}` : ' '}
        </span>
        <button type="button" onClick={(e) => { e.stopPropagation(); onClose(); }} data-testid="artwork-lightbox-close" className="pointer-events-auto p-2 border border-[#1f1f1f] hover:border-white text-[#c8c8c8] hover:text-white transition-colors" aria-label="Close">
          <X className="w-4 h-4" />
        </button>
      </div>

      <motion.img
        key={image.image_url}
        src={image.image_url}
        alt={image.alt_text || ''}
        initial={{ scale: 0.98, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.35 }}
        onClick={(e) => e.stopPropagation()}
        className="max-w-[92vw] max-h-[86vh] object-contain"
      />

      {hasMany && (
        <>
          <button onClick={(e) => { e.stopPropagation(); prev(); }} aria-label="Previous" data-testid="artwork-lightbox-prev" className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 p-3 border border-[#1f1f1f] hover:border-white text-[#c8c8c8] hover:text-white transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); next(); }} aria-label="Next" data-testid="artwork-lightbox-next" className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 p-3 border border-[#1f1f1f] hover:border-white text-[#c8c8c8] hover:text-white transition-colors">
            <ChevronRight className="w-5 h-5" />
          </button>
        </>
      )}
    </motion.div>
  );
}

// ─── Inquiry modal (unchanged flow) ───────────────────────────

function InquiryModal({ artwork, onClose, success, setSuccess }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) { toast.error('Name and email are required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/artworks/${artwork.slug}/inquiry`, { name, email, country, message });
      setSuccess(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send inquiry');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] bg-[#050505]/85 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={onClose}
      data-testid="pw-inquiry-modal"
    >
      <motion.div
        initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }} transition={{ duration: 0.3 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md bg-[#0a0a0a] border border-[#1a1a1a] p-6 md:p-8"
      >
        <button onClick={onClose} className="absolute top-3 right-3 p-1.5 border border-transparent hover:border-[#333] text-[#666] hover:text-white transition-colors">
          <X className="w-4 h-4" />
        </button>

        {!success ? (
          <>
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#90E0EF]">Inquire</div>
            <h2 className="mt-2 text-xl font-outfit font-semibold text-white tracking-tight">{artwork.title}</h2>
            <p className="mt-1 text-[11px] font-mono text-[#666]">{artwork.artist?.name}</p>

            <form onSubmit={submit} className="mt-6 space-y-4">
              <Field label="Name">
                <input data-testid="pw-inq-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-[#050505] border border-[#1a1a1a] px-3 py-2.5 text-[13px] text-[#f6f6f6] focus:outline-none focus:border-[#90E0EF]/40" required />
              </Field>
              <Field label="Email">
                <input data-testid="pw-inq-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-[#050505] border border-[#1a1a1a] px-3 py-2.5 text-[13px] text-[#f6f6f6] focus:outline-none focus:border-[#90E0EF]/40" required />
              </Field>
              <Field label="Country">
                <input data-testid="pw-inq-country" value={country} onChange={(e) => setCountry(e.target.value)} placeholder="Optional" className="w-full bg-[#050505] border border-[#1a1a1a] px-3 py-2.5 text-[13px] text-[#f6f6f6] placeholder:text-[#444] focus:outline-none focus:border-[#90E0EF]/40" />
              </Field>
              <Field label="Message (optional)">
                <textarea data-testid="pw-inq-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder="Anything specific you'd like us to know?" className="w-full bg-[#050505] border border-[#1a1a1a] px-3 py-2.5 text-[13px] text-[#f6f6f6] placeholder:text-[#444] focus:outline-none focus:border-[#90E0EF]/40 resize-none" />
              </Field>
              <button data-testid="pw-inq-submit" type="submit" disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] px-6 py-4 bg-[#90E0EF] text-[#050505] hover:bg-white transition-colors disabled:opacity-50">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {submitting ? 'Sending…' : 'Send inquiry'}
              </button>
            </form>
          </>
        ) : (
          <div className="py-8 text-center" data-testid="pw-inq-success">
            <CheckCircle2 className="w-10 h-10 text-[#7ed4a0] mx-auto" />
            <h3 className="mt-4 text-lg font-outfit font-semibold text-white">Thank you.</h3>
            <p className="mt-2 text-[13px] text-[#c8c8c8] leading-relaxed">Our team will get in touch with you to fulfill the order.</p>
            <button onClick={onClose} className="mt-6 text-[10px] font-mono uppercase tracking-[0.2em] text-[#666] hover:text-white transition-colors">Close</button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] font-mono uppercase tracking-[0.2em] text-[#777] mb-1.5">{label}</label>
      {children}
    </div>
  );
}
